import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfile } from "@/hooks/useProfile";
import {
  pushUserAuthenticated,
  pushUserLoggedOut,
  pushUserProperties,
  getAccountType,
  getPaidTier,
  getProjectCountBucket,
  pushSignUpCompleted,
  pushProjectActivated,
  pushSubscriptionCanceled,
} from "@/lib/analytics";

const RouteChangeListener = () => {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const { 
    tier, 
    isLoading: subLoading,
    subscriptionId,
    billingCycle,
    cancelAtPeriodEnd,
    canceledAt,
    subscriptionStartedAt,
    cancellationReason,
  } = useSubscription();
  const { 
    profile, 
    isLoading: profileLoading, 
    markSignupEventSent, 
    markActivationEventSent,
    markCancellationTracked,
  } = useProfile();
  const hasTrackedUser = useRef(false);
  const hasTrackedSignup = useRef(false);
  const hasTrackedActivation = useRef(false);
  const hasTrackedCancellation = useRef(false);
  const prevUserRef = useRef<string | null>(null);
  const prevTierRef = useRef<string | null>(null);
  const prevActivatedRef = useRef<boolean | null>(null);
  const prevBucketRef = useRef<string | null>(null);
  const prevProjectBucketRef = useRef<string | null>(null);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Virtual pageview on every route change
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "virtual_pageview",
      page_path: location.pathname + location.search,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location]);

  // User authenticated event - once per session when auth hydrates
  useEffect(() => {
    if (!isLoading && user && !hasTrackedUser.current) {
      pushUserAuthenticated(user.id);
      hasTrackedUser.current = true;
      prevUserRef.current = user.id;
    }
    // User logged out - was authenticated, now isn't
    if (!isLoading && !user && prevUserRef.current) {
      pushUserLoggedOut();
      prevUserRef.current = null;
      hasTrackedUser.current = false;
    }
  }, [user, isLoading]);

  // User properties - push when subscription/profile state loads or changes
  useEffect(() => {
    if (!subLoading && !profileLoading && user && tier) {
      const isActivated = profile?.is_activated ?? false;
      const bucket = profile?.days_to_activation_bucket ?? null;
      const projectBucket = profile?.lifetime_project_count 
        ? getProjectCountBucket(profile.lifetime_project_count) ?? null
        : null;
      
      // Push if tier, activation status, bucket, or project bucket changed
      if (
        prevTierRef.current !== tier || 
        prevActivatedRef.current !== isActivated || 
        prevBucketRef.current !== bucket ||
        prevProjectBucketRef.current !== projectBucket
      ) {
        const accountType = getAccountType(tier);
        const paidTier = getPaidTier(tier);
        pushUserProperties({
          account_type: accountType,
          paid_tier: paidTier,
          is_activated: isActivated,
          // Only include bucket if activated and bucket exists
          ...(isActivated && bucket && { activation_days_bucket: bucket }),
          // Include project bucket if it exists (omit when 0)
          ...(projectBucket && { project_count_bucket: projectBucket }),
        });
        prevTierRef.current = tier;
        prevActivatedRef.current = isActivated;
        prevBucketRef.current = bucket;
        prevProjectBucketRef.current = projectBucket;
      }
    }
    // Reset when user logs out
    if (!user) {
      prevTierRef.current = null;
      prevActivatedRef.current = null;
      prevBucketRef.current = null;
      prevProjectBucketRef.current = null;
    }
  }, [tier, subLoading, user, profile, profileLoading]);

  // Keep Sentry plan_tier tag in sync with the resolved subscription tier.
  useEffect(() => {
    if (user && !subLoading && tier) {
      Sentry.setTag("plan_tier", tier);
    }
  }, [user, subLoading, tier]);

  // Sign up completed event - fires once per user after first authentication
  useEffect(() => {
    console.log('[SignupEvent] Checking conditions:', {
      isLoading,
      profileLoading,
      hasUser: !!user,
      hasProfile: !!profile,
      signupEventSent: profile?.signup_event_sent,
      hasTrackedSignup: hasTrackedSignup.current
    });
    
    if (isLoading || profileLoading || !user || !profile) return;

    if (!profile.signup_event_sent && !hasTrackedSignup.current) {
      const billing: "monthly" | "annual" = 
        (profile.signup_billing === 'monthly' || profile.signup_billing === 'annual')
          ? profile.signup_billing
          : 'monthly';
      console.log('[SignupEvent] Firing sign_up_completed with billing_cycle:', billing);
      hasTrackedSignup.current = true;
      pushSignUpCompleted({ billing_cycle: billing });
      markSignupEventSent();
      markSignupEventSent();
    }
  }, [user, profile, isLoading, profileLoading, markSignupEventSent]);

  // Project activated event - fires once per user lifetime when first stakeholder comment received
  useEffect(() => {
    console.log('[ActivationEvent] Checking conditions:', {
      isLoading,
      profileLoading,
      hasUser: !!user,
      hasProfile: !!profile,
      is_activated: profile?.is_activated,
      activation_event_sent: profile?.activation_event_sent,
      hasTrackedActivation: hasTrackedActivation.current
    });
    
    if (isLoading || profileLoading || !user || !profile) return;

    // Only fire if user is activated but event hasn't been sent yet
    if (profile.is_activated && !profile.activation_event_sent && !hasTrackedActivation.current) {
      console.log('[ActivationEvent] Firing project_activated');
      hasTrackedActivation.current = true;
      pushProjectActivated();
      markActivationEventSent();
    }
  }, [user, profile, isLoading, profileLoading, markActivationEventSent]);

  // Subscription canceled event - fires when backend confirms cancellation
  useEffect(() => {
    console.log('[CancellationEvent] Checking conditions:', {
      isLoading,
      subLoading,
      profileLoading,
      hasUser: !!user,
      hasProfile: !!profile,
      subscriptionId,
      cancelAtPeriodEnd,
      canceledAt,
      last_cancellation_id_tracked: profile?.last_cancellation_id_tracked,
      hasTrackedCancellation: hasTrackedCancellation.current
    });

    if (isLoading || subLoading || profileLoading || !user || !profile) return;

    // Detect cancellation: either immediate (canceledAt set) or scheduled (cancelAtPeriodEnd)
    const isCanceled = canceledAt !== null || cancelAtPeriodEnd;
    if (!isCanceled || !subscriptionId) return;

    // Check if already tracked for this subscription
    if (profile.last_cancellation_id_tracked === subscriptionId || hasTrackedCancellation.current) return;

    // Calculate days_active if we have both dates
    let daysActive: number | undefined;
    if (subscriptionStartedAt && canceledAt) {
      const start = new Date(subscriptionStartedAt);
      const cancel = new Date(canceledAt);
      daysActive = Math.floor((cancel.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    }

    console.log('[CancellationEvent] Firing subscription_canceled');
    hasTrackedCancellation.current = true;

    // Push user properties FIRST (account_type -> free, paid_tier -> none)
    pushUserProperties({
      account_type: "free",
      paid_tier: "none",
    });

    // Fire subscription_canceled event
    pushSubscriptionCanceled({
      plan: tier as "starter" | "pro" | "unlimited",
      billing_cycle: billingCycle || "monthly",
      cancellation_effective: cancelAtPeriodEnd ? "period_end" : "immediate",
      subscription_id: subscriptionId,
      cancel_reason: cancellationReason || undefined,
      days_active: daysActive,
    });

    // Mark as tracked in database
    markCancellationTracked(subscriptionId);
  }, [
    user, profile, isLoading, subLoading, profileLoading,
    subscriptionId, tier, billingCycle, cancelAtPeriodEnd, canceledAt,
    subscriptionStartedAt, cancellationReason, markCancellationTracked
  ]);

  return null;
};

export default RouteChangeListener;
